import type { CSSProperties, ReactNode } from 'react'

import { CopyPublicLinkButton } from '@/components/public/copy-public-link-button'
import { PublicEntityHeader } from '@/components/public/public-entity-header'
import { PublicEntityHero } from '@/components/public/public-entity-hero'
import { PublicOpenRolesSection } from '@/components/public/public-open-roles-section'
import { badgeVariants } from '@/components/ui/badge'
import { MarkdownBody } from '@/components/ui/markdown-content'
import { PublicContactStrip } from '@/components/ui/entity-contact-sections'
import { extraSocialContactFrom } from '@/lib/social-networks'
import { EntityLogo } from '@/components/ui/entity-logo'
import { EntityOrganisationFactsBlock, EntityOrganisationCompanyFactsRow } from '@/components/entity-organisation-facts-block'
import { OrganisationLocationsSection } from '@/components/organisation/organisation-locations-section'
import { EntityIdentityCardMetadata } from '@/components/entity-identity-card'
import { getPluginSlots } from '@/plugin/slots'
import { cn } from '@/lib/utils'
import type { PublicJob, PublicOrganisation } from '@/types/public-share'

function PublicHiringNowLink({ count }: { count: number }) {
  const label = count === 1 ? 'Hiring now · 1 open role' : `Hiring now · ${count} open roles`
  return (
    <a
      href="#open-roles"
      className={cn(
        badgeVariants({ variant: 'successSoft' }),
        'relative isolate w-fit shrink-0 gap-1.5 font-medium no-underline',
        'transition-transform duration-150 ease-out active:scale-[0.96]',
        'after:absolute after:inset-x-0 after:top-1/2 after:h-11 after:-translate-y-1/2 after:content-[""]',
      )}
    >
      <span className="size-1.5 shrink-0 rounded-full bg-success" aria-hidden />
      {label}
    </a>
  )
}

export type OrganisationPublicProfileContentProps = {
  organisation: PublicOrganisation
  /** Defaults to the public share URL. */
  shareUrl?: string
  actions?: ReactNode
  jobHref?: (job: PublicJob) => string
  jobLinkState?: (job: PublicJob) => unknown
  /** Extra classes on the open-roles section (e.g. drop marketing scroll offset in-app). */
  rolesClassName?: string
}

/** Banner + overlapping logo for the public organisation template. */
export function OrganisationPublicProfileHero({ organisation }: { organisation: PublicOrganisation }) {
  return (
    <PublicEntityHero
      bannerUrl={organisation.banner_url}
      identity={
        <EntityLogo
          src={organisation.logo_url}
          alt={`${organisation.name} logo`}
          size="hero"
          framed
        />
      }
    />
  )
}

/** Identity, about, locations, and open roles — shared by guest `/o` and in-app candidate view. */
export function OrganisationPublicProfileSections({
  organisation,
  shareUrl,
  actions,
  jobHref,
  jobLinkState,
  rolesClassName,
}: OrganisationPublicProfileContentProps) {
  const roles = organisation.open_roles ?? []
  const hasDescription = Boolean(organisation.description?.trim())
  const hasRoles = roles.length > 0
  const publicUrl = shareUrl ?? organisation.public_url ?? ''

  return (
    <div className="space-y-8">
      <PublicEntityHeader
        title={
          <div className="flex min-w-0 flex-wrap items-center gap-4">
            <h1 className="text-3xl font-semibold tracking-tight">{organisation.name}</h1>
            {hasRoles ? <PublicHiringNowLink count={roles.length} /> : null}
          </div>
        }
        clearance="none"
        className="pt-14"
        titleAlign="center"
        actions={
          actions ?? (publicUrl ? <CopyPublicLinkButton url={publicUrl} iconOnly /> : null)
        }
      >
        <EntityIdentityCardMetadata className="space-y-4">
          <EntityOrganisationFactsBlock
            industry={organisation.industry}
            company_size={organisation.company_size}
            founded_year={organisation.founded_year}
            city={organisation.city}
            country={organisation.country}
            locations={organisation.locations}
            industryLocationLayout="inline"
            showCompanyFacts={false}
          />
          <PublicContactStrip
            website={organisation.website}
            linkedin_url={organisation.linkedin_url}
            x_url={organisation.x_url}
            websitePresentation="visitSite"
            {...extraSocialContactFrom(organisation)}
          />
          <EntityOrganisationCompanyFactsRow
            company_size={organisation.company_size}
            founded_year={organisation.founded_year}
          />
        </EntityIdentityCardMetadata>
      </PublicEntityHeader>
      {hasDescription ? (
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">About</h2>
          <MarkdownBody
            className="!text-base leading-relaxed"
            plainClassName="text-base leading-relaxed text-muted-foreground"
          >
            {organisation.description}
          </MarkdownBody>
        </section>
      ) : null}
      <OrganisationLocationsSection locations={organisation.locations} emptyVariant="hide" />
      {hasRoles ? (
        <PublicOpenRolesSection
          roles={roles}
          jobHref={jobHref}
          jobLinkState={jobLinkState}
          className={rolesClassName}
        />
      ) : (
        <p className="text-muted-foreground">No published roles right now.</p>
      )}
    </div>
  )
}

/** Full public-org column: wide banner, narrower reading body when the tenant sets a share max width. */
export function OrganisationPublicProfileStack({
  organisation,
  ...sectionProps
}: OrganisationPublicProfileContentProps) {
  const shareMax = getPluginSlots().publicShareBodyMaxWidthPx
  const stackStyle =
    shareMax != null
      ? ({ '--public-share-body-max-width': `${shareMax}px` } as CSSProperties)
      : undefined

  return (
    <div className="space-y-8" style={stackStyle}>
      <OrganisationPublicProfileHero organisation={organisation} />
      {shareMax != null ? (
        <div className="mx-auto w-full" style={{ maxWidth: shareMax }}>
          <OrganisationPublicProfileSections organisation={organisation} {...sectionProps} />
        </div>
      ) : (
        <OrganisationPublicProfileSections organisation={organisation} {...sectionProps} />
      )}
    </div>
  )
}
